import React, { useRef } from 'react'
import { isNil } from 'lodash'

import { ConversationBlock } from './ConversationBlock'
import { SilenceBlock } from './SilenceBlock'
import { useTranscript } from './TranscriptContext'
import { CONVERSATION, SILENCE } from './transcript.constants'

import './TranscriptBlocks.scss'

// How far the pointer may travel between press and release and still count as a
// click asking to seek, rather than the tail of a drag-select.
const DRAG_SLOP_PX = 4

export const TranscriptBlocks = ({
  transcript = [],
  transcriptBlockRef = { current: null },
  showTranscriptHypothesis,
  handleTimestampClick,
  searchQuery,
  hardSelectedEvent,
  metadata,
  searchMatches = [],
  currentSearchIndex = -1,
  matchIndicesMap = {},
  highlightedTranscriptIndex,
  isTranslating = false,
  comments = null,
}) => {
  const { isComposingComment } = useTranscript()
  // Where the pointer went down, so the click that follows can be told apart
  // from the end of a drag-select.
  //
  // The old guard read window.getSelection().isCollapsed at click time, which
  // never worked in a real browser: committing the selection re-renders the
  // turn to paint the draft highlight, React replaces the text nodes the Range
  // points at, and the selection collapses before the click arrives. The guard
  // then saw "nothing selected" and seeked — the restart-on-every-selection bug
  // in AVR-875. Geometry survives the re-render; the selection does not.
  const pressOriginRef = useRef(null)

  const handleBlockClick = (event, timestamp) => {
    const origin = pressOriginRef.current
    pressOriginRef.current = null
    if (isNil(handleTimestampClick)) {
      return
    }
    // The composer owns the player while it is open (see MediaPlayer): clicking
    // a turn behind it must not move the playhead out from under the comment.
    if (isComposingComment) {
      return
    }
    // detail > 1 is the second press of a double-click, which is selecting a
    // word, not asking to seek.
    if (event.detail > 1) {
      return
    }
    if (
      !isNil(origin) &&
      Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > DRAG_SLOP_PX
    ) {
      return
    }
    handleTimestampClick(timestamp)
  }

  return (
    <div className="transcript-blocks" data-testid="transcript-blocks" ref={transcriptBlockRef}>
      {transcript.map((section, index) => {
        const { type, side, data, timestamp, duration } = section

        if (type === CONVERSATION) {
          return (
            <div
              key={`clickable-conversation-${data[0].timestamp}`}
              className="transcript-scroll-top"
              onMouseDown={(event) => {
                pressOriginRef.current = { x: event.clientX, y: event.clientY }
              }}
              onClick={(event) => handleBlockClick(event, data[0].timestamp)}
            >
              <ConversationBlock
                key={`conversation-${data[0].timestamp}`}
                side={side}
                data={data}
                showHypothesis={showTranscriptHypothesis}
                highlighted={highlightedTranscriptIndex === index}
                searchQuery={searchQuery}
                hardSelectedEvent={hardSelectedEvent}
                metadata={metadata}
                searchMatches={searchMatches}
                currentSearchIndex={currentSearchIndex}
                blockIndex={index}
                matchIndicesMap={matchIndicesMap}
                isTranslating={isTranslating}
                comments={comments}
              />
            </div>
          )
        }

        if (type === SILENCE) {
          return (
            <SilenceBlock
              key={`silence-${timestamp}`}
              duration={duration}
              highlighted={highlightedTranscriptIndex === index}
            />
          )
        }

        return null
      })}
    </div>
  )
}
